import { apiService } from '@/services/api/apiService';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

interface Video {
  id: number;
  title: string;
  description?: string;
  categoryId?: number;
  categoryName?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  durationSeconds?: number;
  maxPoints?: number;
  isPublished?: boolean;
  uploadedBy?: number;
  [key: string]: any;
}

interface VideoCategory {
  id: number;
  categoryName?: string;
  name?: string;
  [key: string]: any;
}

interface VideoState {
  videos: Video[];
  categories: VideoCategory[];
  videoAnalytics: any;
  videoAnalyticsLoading: boolean;
  loading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  error: string | null;
}

const PORT = import.meta.env.VITE_USERS_PORT;
const MASTERURL = import.meta.env.VITE_API_LEARNING_URL;

// Fetch all existing videos (shown as cards on the page)
export const fetchVideos = createAsyncThunk<Video[], void, { rejectValue: string }>(
  'videos/fetchVideos',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.post<{ data: Video[] }>(
        '/get_all_videos',
        {},
        PORT,
        MASTERURL,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

// Fetch all categories (shown in the modal)
export const fetchVideoCategories = createAsyncThunk<
  VideoCategory[],
  void,
  { rejectValue: string }
>('videos/fetchVideoCategories', async (_, { rejectWithValue }) => {
  try {
    const response = await apiService.post<{ data: VideoCategory[] }>(
      '/get_all_categories',
      {},
      PORT,
      MASTERURL,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});

// Create a video
export const createVideo: any = createAsyncThunk(
  'videos/createVideo',
  async (params: any, thunkAPI) => {
    try {
      const response = await apiService.post('create_video', params, PORT, MASTERURL);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// Create a new category inline (used by the "Create Category" option in the modal)
export const createVideoCategory: any = createAsyncThunk(
  'videos/createVideoCategory',
  async (params: any, thunkAPI) => {
    try {
      const response = await apiService.post('create_category', params, PORT, MASTERURL);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// Update an existing video
export const updateVideo: any = createAsyncThunk(
  'videos/updateVideo',
  async (params: any, thunkAPI) => {
    try {
      const response = await apiService.post('update_video', params, PORT, MASTERURL);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// Delete an existing video (pass the video id / uuid in the payload)
export const deleteVideo: any = createAsyncThunk(
  'videos/deleteVideo',
  async (params: any, thunkAPI) => {
    try {
      const response = await apiService.post('delete_video', params, PORT, MASTERURL);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// Search videos with their full analytics (views, completion, users watching).
// Expects a payload like { videoId } and returns the full array of analytics objects.
export const searchVideoAnalytics: any = createAsyncThunk(
  'videos/searchVideoAnalytics',
  async (params: { videoId: any }, thunkAPI) => {
    try {
      const response: any = await apiService.post('search_videos', params, PORT, MASTERURL);
      const list = Array.isArray(response?.data) ? response.data : response;
      return Array.isArray(list) ? list : [];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

const videoSlice = createSlice({
  name: 'videos',
  initialState: {
    videos: [],
    categories: [],
    videoAnalytics: [],
    videoAnalyticsLoading: false,
    loading: false,
    creating: false,
    updating: false,
    deleting: false,
    error: null,
  } as VideoState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchVideos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVideos.fulfilled, (state, action: PayloadAction<Video[]>) => {
        state.loading = false;
        state.videos = action.payload;
      })
      .addCase(fetchVideos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(fetchVideoCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVideoCategories.fulfilled, (state, action: PayloadAction<VideoCategory[]>) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchVideoCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(createVideo.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createVideo.fulfilled, (state, action: PayloadAction<any>) => {
        state.creating = false;
        const created = action?.payload?.data ?? action?.payload;
        if (Array.isArray(created)) {
          state.videos = created;
        } else if (created?.id) {
          state.videos = [created, ...state.videos];
        }
      })
      .addCase(createVideo.rejected, (state, action) => {
        state.creating = false;
        state.error = (action.payload as string) ?? 'Unknown error';
      })
      .addCase(createVideoCategory.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createVideoCategory.fulfilled, (state) => {
        state.creating = false;
      })
      .addCase(createVideoCategory.rejected, (state, action) => {
        state.creating = false;
        state.error = (action.payload as string) ?? 'Unknown error';
      })
      .addCase(updateVideo.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateVideo.fulfilled, (state, action: PayloadAction<any>) => {
        state.updating = false;
        const updated = action?.payload?.data ?? action?.payload;
        if (Array.isArray(updated)) {
          state.videos = updated;
        } else if (updated?.id) {
          state.videos = state.videos.map((v) =>
            String(v.id) === String(updated.id) ? { ...v, ...updated } : v,
          );
        }
      })
      .addCase(updateVideo.rejected, (state, action) => {
        state.updating = false;
        state.error = (action.payload as string) ?? 'Unknown error';
      })
      .addCase(deleteVideo.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteVideo.fulfilled, (state, action: PayloadAction<any>) => {
        state.deleting = false;
        const deleted = action?.payload?.data ?? action?.payload;
        const deletedId = deleted?.id ?? deleted?.videoId;
        if (deletedId) {
          state.videos = state.videos.filter((v) => String(v.id) !== String(deletedId));
        }
      })
      .addCase(deleteVideo.rejected, (state, action) => {
        state.deleting = false;
        state.error = (action.payload as string) ?? 'Unknown error';
      })
      .addCase(searchVideoAnalytics.pending, (state) => {
        state.videoAnalyticsLoading = true;
        state.videoAnalytics = null;
        state.error = null;
      })
      .addCase(searchVideoAnalytics.fulfilled, (state, action: PayloadAction<any>) => {
        state.videoAnalyticsLoading = false;
        const payload = action.payload;
        if (Array.isArray(payload) && payload.length > 0) {
          state.videoAnalytics = payload[0];
        } else {
          state.videoAnalytics = payload;
        }
      })
      .addCase(searchVideoAnalytics.rejected, (state, action) => {
        state.videoAnalyticsLoading = false;
        state.videoAnalytics = null;
        state.error = (action.payload as string) ?? 'Unknown error';
      });
  },
});

export default videoSlice.reducer;
